import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faMinus } from '@fortawesome/pro-solid-svg-icons/faMinus';
import { useState, forwardRef, useRef, useEffect, useMemo } from 'react';
import cn from 'classnames';
import Control from '@ebsco-ui/control';
import '@ebsco-ui/utils';
import { jsx } from 'react/jsx-runtime';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _typeof2(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof2 = function _typeof2(obj) { return typeof obj; }; } else { _typeof2 = function _typeof2(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof2(obj); }

function _typeof(obj) {
  if (typeof Symbol === "function" && _typeof2(Symbol.iterator) === "symbol") {
    _typeof = function _typeof(obj) {
      return _typeof2(obj);
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : _typeof2(obj);
    };
  }

  return _typeof(obj);
}

/** Takes multiple refs and returns a single ref-function that binds element instance to each ref */
var combineRefs = function combineRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }

  return function (component) {
    refs.forEach(function (ref) {
      if (typeof ref === "function") {
        ref(component);
      }

      if (_typeof(ref) === "object" && ref) {
        ref.current = component;
      }
    });
  };
};

var useControlledValue = function useControlledValue(externalValue, defaultValue) {
  var _useState = useState(defaultValue),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];

  return [externalValue !== null && externalValue !== void 0 ? externalValue : value, setValue];
};

var _excluded = ["className", "isIndeterminate", "onChange", "checked"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var Checkbox = forwardRef(function (_ref, forwardedRef) {
  var className = _ref.className,
      _ref$isIndeterminate = _ref.isIndeterminate,
      isIndeterminate = _ref$isIndeterminate === void 0 ? false : _ref$isIndeterminate,
      handleChange = _ref.onChange,
      externalChecked = _ref.checked,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useControlledValue = useControlledValue(externalChecked, false),
      _useControlledValue2 = _slicedToArray(_useControlledValue, 2),
      checked = _useControlledValue2[0],
      setChecked = _useControlledValue2[1];

  var checkboxRef = useRef(null);
  useEffect(function () {
    if (checkboxRef && checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);
  var mergedRefs = combineRefs(forwardedRef, checkboxRef);
  var icon = useMemo(function () {
    if (isIndeterminate && !checked) {
      return faMinus;
    }

    return faCheck;
  }, [checked, isIndeterminate]);
  return /*#__PURE__*/jsx(Control, _objectSpread({
    checked: checked,
    className: cn("eb-checkbox", className),
    icon: icon,
    onChange: function onChange(e) {
      handleChange && handleChange(e);
      setChecked(function (prevChecked) {
        return !prevChecked;
      });
    },
    ref: mergedRefs,
    type: "checkbox"
  }, rest));
});
Checkbox.displayName = "Checkbox";

export { Checkbox, Checkbox as default };
//# sourceMappingURL=checkbox-esm.js.map
